import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Login from './pages/Login';
import Register from './pages/Register';
import AcceptInvitation from './pages/AcceptInvitation';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import RoundPicker from './pages/RoundPicker';
import ManageUsers from './pages/ManageUsers';
import ManageSeasons from './pages/ManageSeasons';
import AuditLog from './pages/AuditLog';
import Settings from './pages/Settings';
import SecurityEvents from './pages/admin/SecurityEvents';
import AdminDropdown from './components/AdminDropdown';
import './App.css';

// Protected Route Component
function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return isAuthenticated ? children : <Navigate to="/login" />;
}

// Layout wrapper with navigation
function DashboardLayout({ children }) {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="dashboard">
      <nav className="navbar">
        <div className="navbar-content">
          <h2>🏉 Marjorie's Footy Tipping</h2>
        </div>
        <div className="navbar-user">
          <span>Welcome, {user?.displayName || user?.username}!</span>
          <button onClick={() => navigate('/settings')} className="btn-settings">
            Settings
          </button>
          {isAdmin() && <AdminDropdown />}
          <button onClick={logout} className="btn-logout">
            Logout
          </button>
        </div>
      </nav>
      {children}
    </div>
  );
}

// Admin Route Component - requires admin role
function AdminRoute({ children }) {
  const { isAuthenticated, isAdmin, loading } = useAuth();

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  if (!isAdmin()) {
    return <Navigate to="/picks" />;
  }

  return children;
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<Login />} />
          {process.env.REACT_APP_ENABLE_REGISTRATION === 'true' && (
            <Route path="/register" element={<Register />} />
          )}
          <Route path="/accept-invitation" element={<AcceptInvitation />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route
            path="/picks"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <RoundPicker />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <AdminRoute>
                <DashboardLayout>
                  <ManageUsers />
                </DashboardLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/admin/seasons"
            element={
              <AdminRoute>
                <DashboardLayout>
                  <ManageSeasons />
                </DashboardLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/admin/audit"
            element={
              <AdminRoute>
                <DashboardLayout>
                  <AuditLog />
                </DashboardLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/admin/security-events"
            element={
              <AdminRoute>
                <DashboardLayout>
                  <SecurityEvents />
                </DashboardLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <Settings />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route path="/" element={<Navigate to="/picks" />} />
        </Routes>
      </AuthProvider>
    </Router>
  );
}

export default App;
